import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Callout } from "@/shared/ui/callout";
import type { SettingsChangeSummary } from "@/features/deployment-settings/model/admin-settings";

interface ThresholdConfirmationDialogProps {
  open: boolean;
  isSaving: boolean;
  deploymentName: string;
  changes: readonly SettingsChangeSummary[];
  onClose: () => void;
  onConfirm: () => void;
}

export function ThresholdConfirmationDialog({
  open,
  isSaving,
  deploymentName,
  changes,
  onClose,
  onConfirm,
}: ThresholdConfirmationDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={(_, _reason) => {
        if (!isSaving) onClose();
      }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="threshold-confirmation-title"
      aria-busy={isSaving}
    >
      <DialogTitle id="threshold-confirmation-title">
        Review deployment configuration changes
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DialogContentText>
          You are about to apply the following changes for <strong>{deploymentName}</strong>. Please
          review them before applying.
        </DialogContentText>
        {changes.some((change) => change.category === "Area capacity") && (
          <Callout
            severity="warning"
            title="Area capacity changes affect alert calculations"
            icon={<WarningAmberOutlinedIcon />}
          >
            Changing an area&apos;s capacity changes the values used to calculate population for
            queues and areas. These changes may cause alerts to trigger or resolve. Thresholds are
            defined on the Thresholds settings page.
          </Callout>
        )}
        <Stack spacing={1}>
          <Typography variant="overline" color="text.secondary">
            Changes to be saved
          </Typography>
          <TableContainer
            sx={{
              maxHeight: 280,
              overflowY: "auto",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table size="small" aria-label="Deployment configuration changes">
              <TableHead>
                <TableRow>
                  <TableCell>Area</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Before</TableCell>
                  <TableCell>After Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changes.map((change) => (
                  <TableRow key={`${change.category}-${change.label}`}>
                    <TableCell>{change.area}</TableCell>
                    <TableCell component="th" scope="row">
                      {change.field}
                    </TableCell>
                    <TableCell>{change.before}</TableCell>
                    <TableCell>{change.after}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Go back
        </Button>
        <Button variant="contained" onClick={onConfirm} disabled={isSaving}>
          {isSaving ? "Applying…" : "Apply changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
